import React from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../ui/utils';
import type {
  PerformanceCategory,
  PerformanceDepartmentGoal,
  PerformanceGoal,
  PerformanceSchedule,
} from '../../types/performance.types';
import { formatPerformanceDate } from '../../utils/performance';

type Mode = 'KeyResults' | 'Percent' | 'Target';

interface NewGoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule: PerformanceSchedule;
  categories: PerformanceCategory[];
  departmentGoals: PerformanceDepartmentGoal[];
  goals: PerformanceGoal[];
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}

const emptyKeyResult = () => ({ title: '' });

export const NewGoalDialog: React.FC<NewGoalDialogProps> = ({
  open,
  onOpenChange,
  schedule,
  categories,
  departmentGoals,
  goals,
  onSubmit,
}) => {
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [categoryId, setCategoryId] = React.useState<string>('');
  const [departmentGoalId, setDepartmentGoalId] = React.useState<string>('');
  const [startDate, setStartDate] = React.useState(schedule.goalOpenDate);
  const [endDate, setEndDate] = React.useState(schedule.endDate);
  const [weight, setWeight] = React.useState('');
  const [mode, setMode] = React.useState<Mode>('Percent');
  const [keyResults, setKeyResults] = React.useState([emptyKeyResult()]);
  const [target, setTarget] = React.useState('');
  const [unit, setUnit] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [tried, setTried] = React.useState(false);

  const remaining = Math.max(
    0,
    100 - goals.filter((goal) => goal.status !== 'Withdrawn').reduce((sum, goal) => sum + goal.weight, 0)
  );

  React.useEffect(() => {
    if (!open) return;
    const active = categories.filter((category) => category.active);
    setTitle('');
    setDescription('');
    setCategoryId(active[0] ? String(active[0].id) : '');
    setDepartmentGoalId('');
    setStartDate(schedule.goalOpenDate);
    setEndDate(schedule.endDate);
    setWeight('');
    setMode('Percent');
    setKeyResults([emptyKeyResult()]);
    setTarget('');
    setUnit('');
    setTried(false);
  }, [open, categories, schedule]);

  const dateError =
    startDate < schedule.startDate || endDate > schedule.endDate
      ? `Dates must sit inside the schedule (${formatPerformanceDate(schedule.startDate)} – ${formatPerformanceDate(schedule.endDate)}).`
      : startDate > endDate
        ? 'End date must be after the start date.'
        : '';

  const weightNumber = weight === '' ? null : Number(weight);
  const weightError =
    weightNumber == null
      ? `Enter a weight (${schedule.minWeight}–${Math.min(schedule.maxWeight, remaining)}%).`
      : weightNumber < schedule.minWeight || weightNumber > schedule.maxWeight
        ? `Each goal must be ${schedule.minWeight}–${schedule.maxWeight}%.`
        : weightNumber > remaining
          ? `Only ${remaining}% is available.`
          : '';

  const errors: string[] = [];
  if (!title.trim()) errors.push('goal title');
  if (!categoryId) errors.push('category');
  if (dateError) errors.push('dates');
  if (mode === 'KeyResults' && !keyResults.some((kr) => kr.title.trim())) errors.push('a key result');
  if (mode === 'Target' && !(Number(target) > 0)) errors.push('a target number');
  if (weightError) errors.push('a valid weight');

  const setKr = (index: number, value: string) =>
    setKeyResults((rows) => rows.map((row, i) => (i === index ? { title: value } : row)));

  const submit = async () => {
    setTried(true);
    if (errors.length) return;
    setSaving(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        categoryId: Number(categoryId),
        departmentGoalId: departmentGoalId ? Number(departmentGoalId) : null,
        startDate,
        endDate,
        weight: Number(weight),
        progressMode: mode,
        keyResults:
          mode === 'KeyResults'
            ? keyResults.filter((kr) => kr.title.trim()).map((kr) => ({ title: kr.title.trim() }))
            : [],
        target: mode === 'Target' ? Number(target) : 0,
        unit: mode === 'Target' ? unit.trim() : '',
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const activeCategories = categories.filter((category) => category.active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] sm:max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Goal</DialogTitle>
          <DialogDescription>
            Align the goal to your department where it fits. Weights must total 100% before the
            plan can be submitted.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 py-2 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="goal-title">Goal title</Label>
              <Input
                id="goal-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Win 5 civil tenders over $1M"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="goal-description">Description</Label>
              <Textarea
                id="goal-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="What does success look like?"
                rows={3}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Category</Label>
              <div className="flex flex-wrap gap-1.5">
                {activeCategories.map((category) => {
                  const selected = String(category.id) === categoryId;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => setCategoryId(String(category.id))}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                        selected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-card text-foreground hover:bg-accent'
                      )}
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: category.colour }}
                      />
                      {category.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Align to a department goal</Label>
              <Select
                value={departmentGoalId || 'none'}
                onValueChange={(value) => setDepartmentGoalId(value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Not aligned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not aligned</SelectItem>
                  {departmentGoals.map((goal) => (
                    <SelectItem key={goal.id} value={String(goal.id)}>
                      {goal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {departmentGoals.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No department goals for your department yet. You can still create a goal.
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="goal-start">Start date</Label>
                <Input
                  id="goal-start"
                  type="date"
                  value={startDate}
                  min={schedule.startDate}
                  max={schedule.endDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="goal-end">End date</Label>
                <Input
                  id="goal-end"
                  type="date"
                  value={endDate}
                  min={schedule.startDate}
                  max={schedule.endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
            </div>
            {(tried && dateError) || dateError ? (
              <p className="text-xs text-destructive">{dateError}</p>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="goal-weight">Weight (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="goal-weight"
                  type="number"
                  min={schedule.minWeight}
                  max={Math.min(schedule.maxWeight, remaining)}
                  value={weight}
                  onChange={(event) => setWeight(event.target.value)}
                  className="w-24 text-right tabular-nums"
                />
                <span className="text-xs text-muted-foreground">
                  {remaining}% available · {Math.max(0, remaining - (weightNumber || 0))}% left after
                  this goal
                </span>
              </div>
              {weightError && (tried || weight !== '') ? (
                <p className="text-xs text-destructive">{weightError}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label>Progress measured by</Label>
              <div className="inline-flex rounded-full bg-muted p-1">
                {(
                  [
                    ['KeyResults', 'Key results'],
                    ['Percent', 'Percentage'],
                    ['Target', 'Target number'],
                  ] as [Mode, string][]
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMode(value)}
                    className={cn(
                      'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                      mode === value
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {mode === 'KeyResults' ? (
              <div className="space-y-2">
                <Label>Key results</Label>
                {keyResults.map((kr, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="w-5 text-xs tabular-nums text-muted-foreground">
                      {index + 1}
                    </span>
                    <Input
                      value={kr.title}
                      onChange={(event) => setKr(index, event.target.value)}
                      placeholder="e.g. Design the pipeline stages"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setKeyResults((rows) =>
                          rows.length === 1 ? [emptyKeyResult()] : rows.filter((_, i) => i !== index)
                        )
                      }
                      aria-label="Remove key result"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setKeyResults((rows) => [...rows, emptyKeyResult()])}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add key result
                </Button>
              </div>
            ) : null}

            {mode === 'Target' ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="goal-target">Target number</Label>
                  <Input
                    id="goal-target"
                    type="number"
                    min={0}
                    value={target}
                    onChange={(event) => setTarget(event.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="goal-unit">Unit</Label>
                  <Input
                    id="goal-unit"
                    value={unit}
                    onChange={(event) => setUnit(event.target.value)}
                    placeholder="tenders"
                  />
                </div>
              </div>
            ) : null}
          </div>

          <aside className="space-y-3 rounded-xl border border-border bg-muted/30 p-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Weight with this goal
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                {remaining === 0 && !weightNumber ? 100 : (weightNumber || 0) + Math.max(0, 100 - remaining)}
                %
              </p>
              <p className="text-xs text-muted-foreground">
                Existing goals hold {100 - remaining}%.
              </p>
            </div>
            <div className="space-y-1.5">
              {goals
                .filter((goal) => goal.status !== 'Withdrawn')
                .map((goal) => (
                  <div key={goal.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate text-muted-foreground">{goal.title}</span>
                    <span className="tabular-nums">{goal.weight}%</span>
                  </div>
                ))}
              {title ? (
                <div className="flex items-center justify-between gap-2 text-xs font-semibold">
                  <span className="truncate">{title}</span>
                  <span className="tabular-nums">{weightNumber || 0}%</span>
                </div>
              ) : null}
            </div>
            {tried && errors.length ? (
              <p className="rounded-[10px] border border-destructive-border bg-destructive-bg p-2 text-xs text-destructive">
                Add or fix: {errors.join(', ')}.
              </p>
            ) : null}
          </aside>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save Goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
